import { logAmplitudeEventAllowCustomers, type EventIdType } from 'amplitude/amplitude'
import { featureConfigSelectors, getPublicFeatureConfig } from 'ducks/featureConfig'
import { getAvailableActions, myEnergySelectors, setProposalData } from 'ducks/myEnergy'
import ActiveDialogContext from 'myenergy/dialogs/ActiveDialogContext'
import { getProjectCashflowCheckoutPath, getProposalUrlAuthToken } from 'projectSections/sections/proposal/utils'
import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type ContextType } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useHistory, useLocation } from 'react-router-dom'
import useProposalData from 'resources/proposalTemplates/proposalV2/useProposalData'
import { isProposalV2Type, ProposalTemplateTypeEnum } from 'resources/proposalTemplates/types'
import restClient from 'restClient'
import type { QuoteMode } from 'types/projects'
import type { ProposalDataType } from 'types/proposals'
import type { RootState } from 'types/state'
import { DialogHelper } from 'util/misc'
import { isQuickQuote } from 'util/quoteMode'
import {
  ensureCheckoutV2ActiveTimeSession,
  flushAndGetCheckoutV2CompletionTiming,
  setCheckoutV2ActiveStep,
  startCheckoutV2ActiveTimeSession,
} from './checkoutV2ActiveTime'
import {
  CHECKOUT_V2_STEP_COMPLETION_EVENT_STEPS,
  CHIP_COLOR_BY_STATUS,
  FEATURE_CONFIG_USE_OPTIMIZED_CHECKOUT_FOR_CASHFLOW_ORGS,
  OPTIMIZED_CHECKOUT_ERRORS,
  OPTIMIZED_CHECKOUT_REQUIRES_ACTIVE_CASHFLOW_ORG,
  type CheckoutChipColor,
  type CheckoutStep,
  type CheckoutStepChipStatus,
  type CheckoutStepIdentifier,
  type OptimizedCheckoutErrorCode,
} from './constants'
import {
  AvailableActionsResponseType,
  CheckoutAnalyticsProposalType,
  CheckoutDetailsResponseType,
  CheckoutPageContextType,
  CheckoutProgressStatus,
  CheckoutStepProgressRow,
  CheckoutStepsProgressResponseType,
  FinanceApplicationTransactionType,
  RefetchCheckoutStepsProgressOptions,
} from './types'

const OS_GENERIC_EXCEPTION_MESSAGE_PREFIX = 'Generic exception '

export function getCheckoutChipColor(
  chipStatus?: CheckoutStepChipStatus,
  chipLabel?: string | null
): CheckoutChipColor {
  if (chipStatus === 'in_progress') {
    return chipLabel?.trim().toLowerCase() === 'approved' ? 'success' : 'primary'
  }
  if (chipStatus && CHIP_COLOR_BY_STATUS[chipStatus]) {
    return CHIP_COLOR_BY_STATUS[chipStatus]
  }
  return 'default'
}

/** Prefer finance steps from checkout progress when they carry chip metadata. */
export function findFinanceProgressStep(checkoutSteps: CheckoutStep[]): CheckoutStep | undefined {
  const candidates = checkoutSteps.filter((s) => s.step === 'get_financed' || s.step === 'explore_finance')
  return candidates.find((s) => s.chip_status != null || s.display_text) ?? candidates[candidates.length - 1]
}

type ResolveResumeCheckoutStepOptions = {
  routingTarget?: 'open_credit_app' | 'open_marketplace' | null
}

/**
 * When re-entering checkout, resume at an incomplete required `get_financed` step.
 * Optional `explore_finance` is left alone — callers keep the timeline-derived step.
 */
export function resolveResumeCheckoutStep(
  checkoutSteps: CheckoutStep[],
  currentStep: CheckoutStep | undefined,
  options?: ResolveResumeCheckoutStepOptions
): CheckoutStep | undefined {
  if (options?.routingTarget) {
    return currentStep
  }

  const getFinancedStep = checkoutSteps.find((step) => step.step === 'get_financed')
  if (!getFinancedStep || getFinancedStep.status === 'complete') {
    return currentStep
  }

  return getFinancedStep
}

type UseResumeCheckoutStepOnLoadOptions = {
  activeStep: CheckoutStep['id']
  setActiveStep: (stepId: CheckoutStep['id']) => void
  checkoutSteps: CheckoutStep[]
  routingTarget?: 'open_credit_app' | 'open_marketplace' | null
  isTransactionsLoading: boolean
  isAvailableActionsLoading: boolean
  isCheckoutStepsProgressLoading: boolean
  isCheckoutDetailsLoading: boolean
  isProposalDataLoading: boolean
}

/** Once on initial load, resume at an incomplete required `get_financed` step when re-entering checkout. */
export function useResumeCheckoutStepOnLoad({
  activeStep,
  setActiveStep,
  checkoutSteps,
  routingTarget,
  isTransactionsLoading,
  isAvailableActionsLoading,
  isCheckoutStepsProgressLoading,
  isCheckoutDetailsLoading,
  isProposalDataLoading,
}: UseResumeCheckoutStepOnLoadOptions): void {
  const hasResolvedResumeStepRef = useRef(false)

  useEffect(() => {
    if (hasResolvedResumeStepRef.current) return

    const isStillLoading =
      isTransactionsLoading ||
      isAvailableActionsLoading ||
      isCheckoutStepsProgressLoading ||
      isCheckoutDetailsLoading ||
      isProposalDataLoading

    if (isStillLoading || checkoutSteps.length === 0) return

    hasResolvedResumeStepRef.current = true

    if (routingTarget) return

    const currentStep = checkoutSteps.find((step) => step.id === activeStep)
    const resumeStep = resolveResumeCheckoutStep(checkoutSteps, currentStep, { routingTarget })
    if (resumeStep && resumeStep.id !== activeStep) {
      setActiveStep(resumeStep.id)
    }
  }, [
    activeStep,
    checkoutSteps,
    routingTarget,
    isTransactionsLoading,
    isAvailableActionsLoading,
    isCheckoutStepsProgressLoading,
    isCheckoutDetailsLoading,
    isProposalDataLoading,
    setActiveStep,
  ])
}

/** Parse OS `HttpResponse(str(OSGenericException))` JSON and legacy `{ error: string }` bodies. */
export function parseCheckoutHttpError(err: unknown): { message?: string; code?: string } {
  const e = err as { body?: Record<string, unknown>; message?: string }
  const payload =
    e?.body && typeof e.body === 'object'
      ? e.body
      : err && typeof err === 'object' && !Array.isArray(err)
        ? (err as Record<string, unknown>)
        : undefined

  if (payload) {
    const code = typeof payload['error-code'] === 'string' ? payload['error-code'] : undefined
    const rawMessage = payload.message
    if (typeof rawMessage === 'string' && rawMessage !== 'Failed to fetch') {
      const message = rawMessage.startsWith(OS_GENERIC_EXCEPTION_MESSAGE_PREFIX)
        ? rawMessage.slice(OS_GENERIC_EXCEPTION_MESSAGE_PREFIX.length)
        : rawMessage
      return { message, code }
    }
    const detail = payload.detail
    if (typeof detail === 'string' && detail.trim()) {
      return { message: detail.trim(), code }
    }
    const legacyError = payload.error
    if (typeof legacyError === 'string') {
      return { message: legacyError, code }
    }
  }

  if (typeof e?.message === 'string' && e.message && e.message !== 'Failed to fetch') {
    return { message: e.message }
  }
  return {}
}

const CHECKOUT_AUTH_SESSION_ERROR_DETAILS = new Set(['Invalid token.'])

export function isCheckoutAuthSessionError(errorMessage: string | undefined): boolean {
  return typeof errorMessage === 'string' && CHECKOUT_AUTH_SESSION_ERROR_DETAILS.has(errorMessage.trim())
}

export function isOptimizedCheckoutBlockedErrorCode(
  errorCode: string | undefined
): errorCode is OptimizedCheckoutErrorCode {
  return errorCode != null && errorCode in OPTIMIZED_CHECKOUT_ERRORS
}

export function shouldShowOptimizedCheckoutBlocked(errorCode: string | undefined, errorMessage?: string): boolean {
  return isOptimizedCheckoutBlockedErrorCode(errorCode) || isCheckoutAuthSessionError(errorMessage)
}

/** Return a template.json key for blocked checkout UI; caller should pass through translate(). */
export function getOptimizedCheckoutBlockedErrorMessage(
  errorCode: string | undefined,
  apiMessage?: string
): string | undefined {
  if (isOptimizedCheckoutBlockedErrorCode(errorCode)) {
    return apiMessage ?? OPTIMIZED_CHECKOUT_ERRORS[errorCode]
  }
  if (isCheckoutAuthSessionError(apiMessage)) {
    return OPTIMIZED_CHECKOUT_ERRORS.INVALID_SESSION
  }
  return undefined
}

const API_URL = window.API_ROOT + '/api'
const restClientInstance = restClient(API_URL)

export const logOptimizedCheckoutEvent = (eventName: EventIdType, eventData: Record<string, unknown> = {}) => {
  if (eventName === 'checkout_v2_entered') {
    startCheckoutV2ActiveTimeSession()
  }

  const completionStep = CHECKOUT_V2_STEP_COMPLETION_EVENT_STEPS[eventName]
  const completionTiming = completionStep ? flushAndGetCheckoutV2CompletionTiming(completionStep) : undefined

  const device_type =
    typeof window !== 'undefined' && window.matchMedia?.('(max-width: 959.95px)')?.matches ? 'mobile' : 'desktop'
  const payload = {
    ...eventData,
    ...completionTiming,
    device_type,
    optimized_checkout: true,
  }
  logAmplitudeEventAllowCustomers(eventName, payload)
}

export function useCheckoutV2StepActiveTime(activeStepIdentifier: CheckoutStepIdentifier | undefined): void {
  useEffect(() => {
    ensureCheckoutV2ActiveTimeSession()
    setCheckoutV2ActiveStep(activeStepIdentifier ?? null)
  }, [activeStepIdentifier])
}

export type CheckoutV2ErrorStepContext = CheckoutStepIdentifier | 'checkout_blocked'

export function logCheckoutV2ErrorEncountered({
  error_message,
  error_code,
  step_context,
  err,
}: {
  error_message?: string
  error_code?: string | null
  step_context: CheckoutV2ErrorStepContext
  /** When set, message/code are taken from the HTTP body with `error_message` as fallback. */
  err?: unknown
}) {
  const parsed = err ? parseCheckoutHttpError(err) : {}
  const trimmedMessage = (parsed.message ?? error_message)?.trim()
  if (!trimmedMessage) return

  const trimmedCode = (error_code ?? parsed.code)?.trim()
  logOptimizedCheckoutEvent('checkout_v2_error_encountered', {
    error_message: trimmedMessage,
    ...(trimmedCode ? { error_code: trimmedCode } : {}),
    step_context,
  })
}

export function getProposalType(inputs: {
  selectedProject?: { is_lite?: boolean | null; quote_mode?: QuoteMode | null } | null
  proposalTemplateSettings?: { type?: ProposalTemplateTypeEnum | string | null } | null
}): CheckoutAnalyticsProposalType {
  if (isQuickQuote(inputs.selectedProject?.quote_mode)) {
    return 'quick quote'
  }
  if (isProposalV2Type(inputs.proposalTemplateSettings?.type ?? undefined)) {
    return 'sales machine proposal'
  }
  return 'classic proposal'
}

export { getProposalType as getCheckoutAnalyticsProposalType }

function parsePublicFeatureConfigData(raw: unknown): unknown {
  if (raw == null) {
    return null
  }
  if (typeof raw === 'string') {
    try {
      return JSON.parse(raw) as unknown
    } catch {
      return null
    }
  }
  return raw
}

export type OptimizedCheckoutEnabledState = {
  enabled: boolean
  isLoading: boolean
}

/** Resolves optimized-checkout flag from loaded feature config. Defaults to false when config is missing. */
export function resolveIsOptimizedCheckoutEnabled(
  featureConfig: unknown,
  orgCashflowActive: boolean,
  orgId?: number,
  countryIso2?: string | null
): boolean {
  if (OPTIMIZED_CHECKOUT_REQUIRES_ACTIVE_CASHFLOW_ORG && !orgCashflowActive) {
    return false
  }
  if (orgId == null) {
    return false
  }

  const config = parsePublicFeatureConfigData(featureConfig)
  if (config == null || typeof config !== 'object' || Array.isArray(config)) {
    return false
  }

  const c = config as Record<string, unknown>
  const overrides = c.org_overrides
  if (overrides != null && typeof overrides === 'object' && !Array.isArray(overrides)) {
    const orgOverride = overrides?.[String(orgId)]
    if (typeof orgOverride === 'boolean') {
      return orgOverride
    }
  }

  const countryKey = (countryIso2 ?? '').trim().toUpperCase()
  if (countryKey) {
    const countryVal = c[countryKey]
    if (typeof countryVal === 'boolean') {
      return countryVal
    }
  }

  const others = c.OTHERS
  if (typeof others === 'boolean') {
    return others
  }

  return false
}

/** Prefer proposal org id (matches proposal CTAs); fall back to auth/contact org id on checkout load. */
export function resolveOptimizedCheckoutFeatureConfigOrgId(
  proposalOrgId?: number,
  authOrgId?: number
): number | undefined {
  return proposalOrgId ?? authOrgId
}

/**
 * Returns whether optimized checkout is enabled for this org and whether feature config is still loading.
 */
export function useIsOptimizedCheckoutEnabled(
  orgCashflowActive: boolean,
  orgId?: number,
  countryIso2?: string | null,
  authReady = true
): OptimizedCheckoutEnabledState {
  const dispatch = useDispatch()
  const featureConfigKey = FEATURE_CONFIG_USE_OPTIMIZED_CHECKOUT_FOR_CASHFLOW_ORGS

  useEffect(() => {
    if (!authReady) {
      return
    }
    dispatch(getPublicFeatureConfig(featureConfigKey))
  }, [dispatch, featureConfigKey, authReady])

  const featureConfig = useSelector((state: RootState) =>
    featureConfigSelectors.getFeatureConfigData(state, featureConfigKey)
  )
  const configLoading = useSelector((state: RootState) =>
    featureConfigSelectors.getFeatureConfigLoading(state, featureConfigKey)
  )
  const isLoading = !authReady || configLoading === undefined || configLoading === true

  const enabled = useMemo(() => {
    if (isLoading) {
      return false
    }
    return resolveIsOptimizedCheckoutEnabled(featureConfig, orgCashflowActive, orgId, countryIso2)
  }, [featureConfig, orgCashflowActive, orgId, countryIso2, isLoading])

  return { enabled, isLoading }
}

/** Loads Redux proposal data and resolves optimized-checkout gate for the full-page checkout route. */
export function useInitializeOptimizedCheckout(options: {
  projectId?: number
  orgCashflowActive: boolean
  orgId?: number
  countryIso2?: string | null
  authReady?: boolean
}): { showOptimizedCheckout: boolean | null; isOptimizedCheckoutEnabled: boolean } {
  const authReady = options.authReady !== false
  const proposalOrgId = useSelector((state: RootState) => myEnergySelectors.getProposalOrgId(state))
  const featureConfigOrgId = resolveOptimizedCheckoutFeatureConfigOrgId(proposalOrgId, options.orgId)
  const { isLoadingReduxProposalData } = useLoadReduxProposalData({
    projectId: options.projectId,
    authReady,
  })
  const { enabled, isLoading } = useIsOptimizedCheckoutEnabled(
    options.orgCashflowActive,
    featureConfigOrgId,
    options.countryIso2,
    authReady
  )
  const showOptimizedCheckout = !authReady || isLoadingReduxProposalData || isLoading ? null : enabled
  return { showOptimizedCheckout, isOptimizedCheckoutEnabled: enabled }
}

export function navigateToProjectCashflowCheckout(
  dispatch: ReturnType<typeof useDispatch>,
  history: ReturnType<typeof useHistory>,
  pathname: string,
  proposalData: ProposalDataType,
  search: ReturnType<typeof useLocation>['search'] = ''
): boolean {
  const projectId = proposalData.selectedProject?.id
  const systemUuid = proposalData.selectedSystem?.uuid
  const paymentOptionId = proposalData.selectedPaymentOption?.id
  const countryIso2 = proposalData.selectedProject?.country_iso2 ?? undefined

  if (projectId == null || !systemUuid || paymentOptionId == null) {
    return false
  }

  logOptimizedCheckoutEvent('checkout_v2_entered', {
    proposal_type: getProposalType({
      selectedProject: proposalData.selectedProject,
      proposalTemplateSettings: proposalData.proposalTemplateSettings,
    }),
    payment_intent: String(
      proposalData.selectedPaymentOption?.payment_type ?? proposalData.selectedPaymentOption?.title ?? ''
    ),
  })
  dispatch(setProposalData(proposalData))
  const urlAuthToken = getProposalUrlAuthToken(search)
  history.push(
    getProjectCashflowCheckoutPath(pathname, projectId, systemUuid, paymentOptionId, countryIso2, urlAuthToken)
  )
  return true
}

/** Re-populate myEnergy.proposalData after MyEnergy unmount clears it on navigation to checkout. */
export function useLoadReduxProposalData(options: { projectId?: number; authReady?: boolean }): {
  isLoadingReduxProposalData: boolean
} {
  const dispatch = useDispatch()
  const proposalOrg = useSelector((state: RootState) => myEnergySelectors.getProposalOrg(state))
  const { data, isLoading, loadProposalData } = useProposalData()
  const proposalData = data == null ? undefined : (data as unknown as ProposalDataType)

  const authReady = options.authReady !== false
  const shouldLoad = authReady && options.projectId != null && proposalOrg == null

  useEffect(() => {
    if (!shouldLoad || options.projectId == null) {
      return
    }
    void loadProposalData({ id: options.projectId })
  }, [shouldLoad, options.projectId, loadProposalData])

  useEffect(() => {
    if (!shouldLoad || !proposalData) {
      return
    }
    dispatch(setProposalData(proposalData))
  }, [shouldLoad, proposalData, dispatch])

  // Stay loading until Redux proposalOrg exists — local `proposalData` is set before dispatch applies.
  const isLoadingReduxProposalData = shouldLoad && (isLoading || !proposalData || proposalOrg == null)

  return { isLoadingReduxProposalData }
}

export const useCheckoutProposalData = (
  projectId?: number,
  systemUuid?: string,
  paymentOptionId?: number
): {
  proposalData: ProposalDataType | undefined
  isProposalDataLoading: boolean
  refetchProposalData: () => Promise<void>
} => {
  const dispatch = useDispatch()
  const { data, isLoading, loadProposalData, handleSelectSystem, handleSelectPayment } = useProposalData()
  const proposalData = data == null ? undefined : (data as unknown as ProposalDataType)

  useEffect(() => {
    if (projectId == null) {
      return
    }
    void loadProposalData({ id: projectId })
  }, [projectId, loadProposalData])

  useEffect(() => {
    if (!proposalData || !systemUuid) {
      return
    }
    if (proposalData.selectedSystem?.uuid === systemUuid) {
      return
    }
    handleSelectSystem(systemUuid)
  }, [proposalData, systemUuid, handleSelectSystem])

  useEffect(() => {
    if (!proposalData || paymentOptionId == null || !systemUuid) {
      return
    }
    // System selection runs in the effect above; payment option selection must apply only after that aligns.
    if (proposalData.selectedSystem?.uuid !== systemUuid) {
      return
    }
    if (proposalData.selectedPaymentOption?.id === paymentOptionId) {
      return
    }
    handleSelectPayment(paymentOptionId)
  }, [proposalData, paymentOptionId, systemUuid, handleSelectPayment])

  useEffect(() => {
    if (!proposalData || projectId == null) {
      return
    }
    if (proposalData.selectedProject?.id !== projectId) {
      return
    }
    dispatch(setProposalData(proposalData))
  }, [dispatch, proposalData, projectId])

  const refetchProposalData = useCallback(async () => {
    if (projectId != null) {
      await loadProposalData({ id: projectId })
    }
  }, [projectId, loadProposalData])

  const isProposalDataLoading = projectId != null && (isLoading || !proposalData)

  return { proposalData, isProposalDataLoading, refetchProposalData }
}

function selectAvailableActionsForSystem(
  allActions: ReturnType<typeof myEnergySelectors.getAvailableCustomerActions>,
  systemUuid?: string,
  paymentOptionId?: number
): AvailableActionsResponseType {
  if (!systemUuid) {
    return { actions_available: [], system_uuid: '' }
  }
  const forSystem = allActions.find((entry) => entry.system_uuid === systemUuid)
  let actions = (forSystem?.actions_available ?? []) as unknown as AvailableActionsResponseType['actions_available']
  if (paymentOptionId != null) {
    actions = actions.filter(
      (action) => action.payment_option_id === paymentOptionId || action.payment_option_id == null
    )
  }
  return {
    actions_available: actions,
    system_uuid: forSystem?.system_uuid ?? systemUuid,
  }
}

export { selectAvailableActionsForSystem }

export const useAvailableActions = (
  orgId?: number,
  projectId?: number,
  systemUuid?: string,
  paymentOptionId?: number
): {
  availableActions: AvailableActionsResponseType
  isLoading: boolean
  isScopedAvailableActionsReady: boolean
  error: string | undefined
  refetch: () => Promise<void>
} => {
  const dispatch = useDispatch()
  const allAvailableCustomerActions = useSelector(myEnergySelectors.getAvailableCustomerActions)
  const isLoading = useSelector(myEnergySelectors.getIsLoading)
  const hasError = useSelector((state: RootState) => state.myEnergy.error)
  const [isScopedAvailableActionsReady, setIsScopedAvailableActionsReady] = useState(false)
  const scopedFetchStartedRef = useRef(false)

  const availableActions = useMemo(
    () => selectAvailableActionsForSystem(allAvailableCustomerActions, systemUuid, paymentOptionId),
    [allAvailableCustomerActions, systemUuid, paymentOptionId]
  )

  useEffect(() => {
    setIsScopedAvailableActionsReady(false)
    scopedFetchStartedRef.current = false
  }, [orgId, projectId, systemUuid, paymentOptionId])

  const refetch = useCallback(() => {
    if (!orgId || !projectId) {
      return Promise.resolve()
    }
    scopedFetchStartedRef.current = true
    dispatch(
      getAvailableActions(orgId, projectId, {
        system_uuid: systemUuid,
        payment_option_id: paymentOptionId,
      })
    )
    return Promise.resolve()
  }, [dispatch, orgId, projectId, systemUuid, paymentOptionId])

  useEffect(() => {
    if (!orgId || !projectId) return
    void refetch()
  }, [orgId, projectId, refetch])

  useEffect(() => {
    if (scopedFetchStartedRef.current && !isLoading) {
      setIsScopedAvailableActionsReady(true)
    }
  }, [isLoading])

  return {
    availableActions,
    isLoading,
    isScopedAvailableActionsReady,
    error: hasError ? 'Failed to load available actions' : undefined,
    refetch,
  }
}

/** If contract is signed externally, and we get a signing event from return URL, assume contract signing is complete by overriding the progress response */
function mergeContractSigningCompleteIntoSteps(
  data: CheckoutStepsProgressResponseType
): CheckoutStepsProgressResponseType {
  const timelineStepKeys = Object.keys(data) as CheckoutStepIdentifier[]
  const contractIndex = timelineStepKeys.indexOf('contract_signing')
  const nextStepKey = contractIndex >= 0 ? timelineStepKeys[contractIndex + 1] : undefined
  const nextStepIsCurrent = nextStepKey != null && data[nextStepKey]?.is_current === true
  const setContractAsCurrent = !nextStepIsCurrent

  const merged: CheckoutStepsProgressResponseType = {}
  for (const stepKey of timelineStepKeys) {
    const step = data[stepKey]
    if (!step) continue
    if (stepKey === 'contract_signing') {
      merged.contract_signing = {
        ...step,
        status: 'complete',
        display_text: 'Signed',
        chip_status: 'complete',
        is_current: setContractAsCurrent,
      }
    } else if (setContractAsCurrent) {
      merged[stepKey] = { ...step, is_current: false }
    } else {
      merged[stepKey] = { ...step }
    }
  }
  return merged
}

export const useCheckoutStepsProgress = (
  orgId?: number,
  projectId?: number,
  systemUuid?: string,
  paymentOptionId?: number,
  options?: { enabled?: boolean; treatContractSigningAsComplete?: boolean }
): {
  checkoutStepsProgress: CheckoutStepsProgressResponseType
  isLoading: boolean
  error: string | undefined
  errorCode: string | undefined
  refetch: (options?: RefetchCheckoutStepsProgressOptions) => Promise<void>
} => {
  const enabled = options?.enabled !== false
  const [checkoutStepsProgress, setCheckoutStepsProgress] = useState<CheckoutStepsProgressResponseType>({})
  const [isLoading, setIsLoading] = useState<boolean>(enabled)
  const [error, setError] = useState<string | undefined>(undefined)
  const [errorCode, setErrorCode] = useState<string | undefined>(undefined)

  const fetchCheckoutStepsProgress = useCallback(
    (refetchOptions?: RefetchCheckoutStepsProgressOptions) => {
      if (!orgId || !projectId || !enabled) {
        return Promise.resolve()
      }

      if (!refetchOptions?.silent) {
        setIsLoading(true)
      }
      setError(undefined)
      setErrorCode(undefined)
      return restClientInstance('CUSTOM_GET', 'custom', {
        url: `orgs/${orgId}/projects/${projectId}/checkout/progress/?system_uuid=${systemUuid}&payment_option_id=${paymentOptionId}`,
      })
        .then((response: any) => {
          let steps: CheckoutStepsProgressResponseType = response.data ?? {}
          // Backend doesn't know if the contract was signed externally, so we need to merge it in if it was.
          if (options?.treatContractSigningAsComplete) {
            steps = mergeContractSigningCompleteIntoSteps(steps)
          }
          setCheckoutStepsProgress(steps)
          setError(undefined)
          setErrorCode(undefined)
        })
        .catch((err: any) => {
          const parsed = parseCheckoutHttpError(err)
          setError(parsed.message)
          setErrorCode(parsed.code)
          setCheckoutStepsProgress({})
        })
        .finally(() => {
          setIsLoading(false)
        })
    },
    [orgId, projectId, systemUuid, paymentOptionId, enabled, options?.treatContractSigningAsComplete]
  )

  useEffect(() => {
    if (!enabled) {
      setCheckoutStepsProgress({})
      setIsLoading(false)
      setError(undefined)
      setErrorCode(undefined)
      return
    }
    if (!orgId || !projectId) return

    void fetchCheckoutStepsProgress()
  }, [orgId, projectId, fetchCheckoutStepsProgress, enabled])

  return { checkoutStepsProgress, isLoading, error, errorCode, refetch: fetchCheckoutStepsProgress }
}

export const useCheckoutDetails = (
  orgId?: number,
  projectId?: number,
  systemUuid?: string,
  paymentOptionId?: number
): {
  checkoutDetails: CheckoutDetailsResponseType | undefined
  isLoading: boolean
  error: string | undefined
  refetch: () => Promise<void>
} => {
  const [checkoutDetails, setCheckoutDetails] = useState<CheckoutDetailsResponseType | undefined>(undefined)
  const [isLoading, setIsLoading] = useState<boolean>(true)
  const [error, setError] = useState<string | undefined>(undefined)

  const fetchCheckoutDetails = useCallback((): Promise<void> => {
    if (!orgId || !projectId) {
      return Promise.resolve()
    }

    setIsLoading(true)
    return restClientInstance('CUSTOM_GET', 'custom', {
      url: `orgs/${orgId}/projects/${projectId}/checkout/details/?system_uuid=${systemUuid}&payment_option_id=${paymentOptionId}`,
    })
      .then((response: any) => {
        setCheckoutDetails(response.data)
      })
      .catch((err: any) => {
        setError(err?.body?.error)
      })
      .finally(() => {
        setIsLoading(false)
      })
  }, [orgId, projectId, systemUuid, paymentOptionId])

  useEffect(() => {
    if (!orgId || !projectId) return

    fetchCheckoutDetails()
  }, [orgId, projectId, fetchCheckoutDetails])

  return { checkoutDetails, isLoading, error, refetch: fetchCheckoutDetails }
}

export const useFinanceApplicationTransactions = (
  orgId?: number,
  projectId?: number,
  systemUuid?: string,
  paymentOptionId?: number
): {
  transactions: FinanceApplicationTransactionType[]
  lenderLogoUrl: string | null | undefined
  isLoading: boolean
  error: string | undefined
  refetch: () => Promise<void>
} => {
  const [transactions, setTransactions] = useState<FinanceApplicationTransactionType[]>([])
  const [lenderLogoUrl, setLenderLogoUrl] = useState<string | null | undefined>(undefined)
  const [isLoading, setIsLoading] = useState<boolean>(false)
  const [error, setError] = useState<string | undefined>(undefined)

  const fetchTransactions = useCallback((): Promise<void> => {
    if (!orgId || !projectId) {
      return Promise.resolve()
    }

    setIsLoading(true)
    return restClientInstance('CUSTOM_GET', 'custom', {
      url: `orgs/${orgId}/projects/${projectId}/checkout/finance_applications/?system_uuid=${systemUuid}&payment_option_id=${paymentOptionId}`,
    })
      .then((response: any) => {
        setTransactions(response.data?.application_results || [])
        setLenderLogoUrl(response.data?.lender_logo_url)
      })
      .catch((err: any) => {
        setError(err?.body?.error)
      })
      .finally(() => {
        setIsLoading(false)
      })
  }, [orgId, projectId, systemUuid, paymentOptionId])

  const allAvailableCustomerActions = useSelector(myEnergySelectors.getAvailableCustomerActions)
  const availableActionsForSystem = useMemo(
    () => selectAvailableActionsForSystem(allAvailableCustomerActions, systemUuid),
    [allAvailableCustomerActions, systemUuid]
  )
  const availableActionsSnapshotRef = useRef<string | undefined>(undefined)
  const showCreditDecisionDialog = useSelector(myEnergySelectors.getShowCreditDecisionDialog)
  const previousShowCreditDecisionDialogRef = useRef(false)

  useEffect(() => {
    fetchTransactions()
  }, [fetchTransactions])

  // Any Redux refresh of available actions (checkout or elsewhere) should refresh finance transactions too.
  useEffect(() => {
    const snapshot = JSON.stringify(availableActionsForSystem.actions_available)
    const previousSnapshot = availableActionsSnapshotRef.current
    availableActionsSnapshotRef.current = snapshot

    if (previousSnapshot === undefined || previousSnapshot === snapshot) {
      return
    }

    void fetchTransactions()
  }, [availableActionsForSystem, fetchTransactions])

  // Application dialogs dispatch `openCreditDecisionDialog` once a decision is recorded — fetch the new transaction.
  useEffect(() => {
    const didOpen = showCreditDecisionDialog && !previousShowCreditDecisionDialogRef.current
    previousShowCreditDecisionDialogRef.current = showCreditDecisionDialog

    if (didOpen) {
      void fetchTransactions()
    }
  }, [showCreditDecisionDialog, fetchTransactions])

  return { transactions, lenderLogoUrl, isLoading, error, refetch: fetchTransactions }
}

/** Finance steps: when a new application transaction id appears, refresh timeline chip status. */
export const useRefetchCheckoutProgressOnNewFinanceTransaction = (
  transactions: FinanceApplicationTransactionType[],
  refetchCheckoutStepsProgress: (options?: RefetchCheckoutStepsProgressOptions) => void | Promise<void>
) => {
  const previousTransactionIdsRef = useRef<Set<number> | undefined>(undefined)

  useEffect(() => {
    const currentIds = new Set(transactions.map((transaction) => transaction.id))
    const previousIds = previousTransactionIdsRef.current
    previousTransactionIdsRef.current = currentIds

    if (previousIds === undefined) {
      return
    }

    const hasNewTransaction = [...currentIds].some((id) => !previousIds.has(id))
    if (hasNewTransaction) {
      void refetchCheckoutStepsProgress({ silent: true })
    }
  }, [transactions, refetchCheckoutStepsProgress])
}

export function isSalesMachineProposal(
  proposalTemplateSettings?: { type?: ProposalTemplateTypeEnum | string | null } | null
): boolean {
  return isProposalV2Type(proposalTemplateSettings?.type ?? undefined)
}

/** Used on the full-page checkout route (e.g. welcome-back dialog), not proposal sidebar CTAs. */
export function getCheckoutProgressStatus(
  progress: CheckoutStepsProgressResponseType | undefined | null
): CheckoutProgressStatus {
  if (!progress || typeof progress !== 'object') {
    return 'not started'
  }
  const entries = Object.entries(progress).filter((entry): entry is [string, CheckoutStepProgressRow] => {
    const [, v] = entry
    return v != null && typeof v === 'object' && !Array.isArray(v) && 'status' in v
  })
  if (entries.length === 0) {
    return 'not started'
  }

  const isStepDone = (step: CheckoutStepProgressRow) => step.status === 'complete' || step.chip_status === 'complete'

  const isStepStarted = (step: CheckoutStepProgressRow) => isStepDone(step) || step.status === 'in_progress'

  const anyNonSystemStarted = entries.some(([stepKey, step]) => stepKey !== 'system_selection' && isStepStarted(step))
  if (!anyNonSystemStarted) {
    return 'not started'
  }

  if (entries.every(([, step]) => isStepDone(step))) {
    return 'complete'
  }

  return 'in progress'
}

type ActiveDialogName = NonNullable<ContextType<typeof ActiveDialogContext>['activeDialog']>

/** Active dialog state for full-page checkout (mirrors BasicLayout's showDialog/closeDialog). */
export function useActiveDialog() {
  const [activeDialog, setActiveDialog] = useState<ActiveDialogName | null>(null)
  const [dialogData, setDialogData] = useState<unknown>(null)
  const [dialogIsFullWidth, setDialogIsFullWidth] = useState(false)

  const showDialog = useCallback((dialogName: ActiveDialogName, data: unknown, isFullWidth: boolean = false) => {
    DialogHelper.afterOpen()
    setActiveDialog(dialogName)
    setDialogData(data)
    setDialogIsFullWidth(isFullWidth)
  }, [])

  const closeDialog = useCallback(() => {
    DialogHelper.beforeClose()
    setActiveDialog(null)
    setDialogData(null)
    setDialogIsFullWidth(false)
  }, [])

  const activeDialogContextValue = useMemo(
    () => ({
      activeDialog,
      dialogData,
      dialogIsFullWidth,
      showDialog,
    }),
    [activeDialog, dialogData, dialogIsFullWidth, showDialog]
  )

  return {
    activeDialog,
    dialogData,
    dialogIsFullWidth,
    showDialog,
    closeDialog,
    activeDialogContextValue,
  }
}

export const CheckoutPageContext = createContext<CheckoutPageContextType>({
  availableActions: { actions_available: [], system_uuid: '' },
  projectId: undefined,
  systemUuid: undefined,
  paymentOptionId: undefined,
  activeStep: 1,
  checkoutSteps: [],
  checkoutDetails: undefined,
  transactions: [],
  lenderLogoUrl: undefined,
  orgDetails: undefined,
  paymentPricingSummary: undefined,
  setPaymentPricingSummary: () => {},
  showCheckoutPayButton: false,
  setShowCheckoutPayButton: () => {},
  isCheckoutPayEnabled: false,
  setIsCheckoutPayEnabled: () => {},
  registerCheckoutPayHandler: () => {},
  triggerCheckoutPay: () => {},
  isAvailableActionsLoading: true,
  isScopedAvailableActionsReady: false,
  isCheckoutDetailsLoading: true,
  isCheckoutStepsProgressLoading: true,
  isTransactionsLoading: false,
  refetchCheckoutDetails: () => {},
  refetchCheckoutStepsProgress: (_options?: RefetchCheckoutStepsProgressOptions) => {},
  refetchAvailableActions: () => {},
  refetchTransactions: () => {},
  proposalData: undefined,
  isProposalDataLoading: false,
  refetchProposalData: async () => {},
  isCheckoutComplete: false,
  requestLeaveToProposal: () => {},
  onNext: undefined,
})

export const useCheckoutPageContext = () => {
  return useContext(CheckoutPageContext)
}
